import {
  HomePageWidgetConfig,
  type HomePageWidgetPosition,
} from 'pages/HomePage/constants';
import { InlineLabelInput, SkeletonPage } from 'components/common';
import { Panel, PickerInput, Text, Button } from '@epam/promo';
import { useArrayDataSource } from '@epam/uui-core';
import css from './PersonalizationPage.module.scss';
import { useState } from 'react';
import {
  useGetHomePageWidgetsQuery,
  useSaveHomePageWidgetsQuery,
} from 'hooks/api/appSettings';
import { WidgetDefinition } from 'api/appSettings.service';

const emptyConfig: HomePageWidgetConfig = {
  cell_0_0: '',
  cell_0_1: '',
  cell_1_0: '',
  cell_1_1: '',
};

export default function HomePagePersonalization() {
  const { personalizedConfig, isLoading, widgets } =
    useGetHomePageWidgetsQuery();
  const { mutate: savePersonalizedConfig, isLoading: isSaving } =
    useSaveHomePageWidgetsQuery();

  const [draftFormData, setDraftFormData] =
    useState<HomePageWidgetConfig | null>(null);
  const formData = draftFormData ?? personalizedConfig ?? emptyConfig;

  const handleChange = (
    position: HomePageWidgetPosition,
    newValue?: string,
  ) => {
    setDraftFormData((prev) => {
      const updatedConfig = {
        ...(prev || personalizedConfig || emptyConfig),
        [position]: newValue,
      };

      return updatedConfig;
    });
  };

  const handleSubmit = () => {
    savePersonalizedConfig(formData, {
      onSuccess: () => {
        setDraftFormData(null);
      },
    });
  };

  const dataSource = useArrayDataSource<
    WidgetDefinition,
    string | undefined,
    unknown
  >(
    {
      items: widgets ?? [],
    },
    [widgets],
  );

  // ========================= Render =========================

  const renderSelect = (
    formData: HomePageWidgetConfig,
    position: HomePageWidgetPosition,
    label: string,
  ) => (
    <InlineLabelInput label={label}>
      <PickerInput
        dataSource={dataSource}
        isDisabled={isSaving}
        selectionMode="single"
        value={formData[position] || undefined}
        onValueChange={(newValue) => {
          handleChange(position, newValue);
        }}
      />
    </InlineLabelInput>
  );

  return isLoading ? (
    <SkeletonPage />
  ) : (
    <>
      <Panel
        cx={{
          [css.form]: true,
        }}>
        <Text
          color="gray90"
          fontSize="24"
          fontWeight="600"
          lineHeight="24"
          rawProps={{ style: { marginBottom: 24 } }}>
          Home Page Widgets
        </Text>

        {renderSelect(formData, 'cell_0_0', 'Top Left')}
        {renderSelect(formData, 'cell_0_1', 'Top Right')}
        {renderSelect(formData, 'cell_1_0', 'Bottom Left')}
        {renderSelect(formData, 'cell_1_1', 'Bottom Right')}

        <Button
          caption={isSaving ? 'Saving...' : 'Save Layout'}
          color="blue"
          isDisabled={isSaving}
          onClick={handleSubmit}
        />
      </Panel>
    </>
  );
}
